import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AccordionDetails, Box, Typography, Grid, CircularProgress } from "@mui/material";
import PageTitle from "../../common/components/PageTitle/PageTitle";
import { PrimaryButton } from "../../common/components/CustomButton";
import { useNavigate } from "react-router-dom";
import {
	StyledAccordion,
	StyledAccordionSummary,
} from "../pricing_list/components/TenderSection/StyledAccordion";
import { BreadCrumbs } from "../../common/components/BreadCrumbs";
import CheckboxesTags from "../../common/components/CheckboxesTags";
import { useEffect, useMemo, useState } from "react";
import { useLazyGetContractsQuery } from "../../requests_cm/gecoContractsService/service";
import { useRtkQueryDynamicEndpoint } from "../../common/hooks/useRtkQueryDynamicEndpoint";
import { PageInfoType } from "../../requests_cm/gecoTypes";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../core/rootReducers";
import {
	updateContractSearch,
	updateContractPeriodSearch,
	updateContractPeriods,
	updateContracts,
} from "./TimeseriesPageSlice";
import { DateRange } from "mui-daterange-picker";
import QueryBuilder from "./components/query_builder";
import { DateRangeSelector } from "./components/timeserie_graph/DateRangeSelector";
import { lightGrey } from "../../core/theme";
import { useFormik } from "formik";
import { useGetFielddataDefinitionsQuery } from "../../requests_cm/gecoReferentialService/service";
import { useGetTimeseriesDataFromGeco } from "./hooks/useGetTimeseriesDataFromGeco";
import {
	ContractPeriod,
	FieldDataDefQuery,
} from "../../requests_cm/gecoReferentialService/types";
import {
	contractTimeserieSchema,
	getAllcombinationFromQueryform,
	groupBy,
} from "./TimeseriesPage.helper";
import { useGetContractPeriods } from "./hooks/useGetContractPeriods";
import {
	ContractDetailType,
	ContractPeriodType,
} from "../../requests_cm/gecoContractsService/types";
import {
	FieldDataQueryResult
} from "../../requests_cm/gecoTimeserieService/types";

import { ViewTypeSelector } from "./components/timeserie_graph/ViewTypeSelector";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/boost";
import { If } from "../../common/components/If";

const style = {
	left: {
		display: "flex",
		flexDirection: "column",
		minWidth: "500px",
		maxWidth: "500px",
	},
	right: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
	},
	wrapper: {
		display: "flex",
		flexDirection: "row",
		flex: 1,
		gap: "24px",
		padding: "16px",
	},
	contractHeader: {
		display: "flex",
		flexDirection: "row",
		flex: 1,
		alignItems: "center",
		justifyContent: "space-between",
	},
	contractDetails: {
		display: "flex",
		flexDirection: "column",
		gap: "16px",
		backgroundColor: "white",
	},
};

export interface Query {
	name: string;
	step: string;
	unit: string;
	subquery?: { subqueryName: string; source: string }[];
}

export interface QuerySetpForm {
	activeQuery: string;
	dateRange: DateRange;
	timezone?: string;
	queryList?: Record<string, Query>;
}


const TimeseriesPage = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const groupByQueries = groupBy(["queryName", "subQueryName", "unit"]);
	const groupByContract = groupBy(["contractName"]);

	const [currentView, setCurrentView] = useState<"ByQuery" | "ByContract">("ByQuery");
	const [timeseriesData, setTimeseriesData] = useState<FieldDataQueryResult[]>([]);

	const contractSearch = useSelector(
		(state: RootState) => state.timeseriesPage.contractSearch
	);
	const selectedContracts: ContractDetailType[] = useSelector(
		(state: RootState) => state.timeseriesPage.contractValues
	);

	const [queryCombinations, setQueryCombinations] = useState<
		(FieldDataDefQuery & ContractPeriod)[]
	>([]);

	const contractPeriodSearch = useSelector(
		(state: RootState) => state.timeseriesPage.contractPeriodSearch
	);
	const selectedContractPeriods = useSelector(
		(state: RootState) => state.timeseriesPage.contractPeriodValues
	);
	const [contractPeriods, setContractPeriods] = useState<
		ContractPeriodType[]
	>([]);
	const [expanded, setExpanded] = useState<boolean>(true);
	const [loading, setLoading] = useState<boolean>(false);

	const [pageInfo, setPageInfo] = useState<PageInfoType>({
		page: 1,
		search: contractSearch,
	});

	const { data: fielddataDefinitions } = useRtkQueryDynamicEndpoint(
		useGetFielddataDefinitionsQuery
	)({});

	const [baseGetContracts, { data: contracts }] = useLazyGetContractsQuery();

	const getContracts = useRtkQueryDynamicEndpoint(baseGetContracts);

	const contractItems = contracts?.result.map((contract) => {
		return {
			id: contract.id,
			name: contract.name,
			start_date: contract.start_date,
			end_date: contract.end_date,
		};
	});

	const getBackToContracts = () => {
		navigate(-1);
	};

	useEffect(() => {
		getContracts(pageInfo);
	}, [pageInfo]);

	let contractTimeserieFormik = useFormik<QuerySetpForm>({
		validationSchema: contractTimeserieSchema,
		initialValues: {
			activeQuery: "",
			dateRange: {},
			timezone: "",
		},
		enableReinitialize: true,
		onSubmit: (values: QuerySetpForm) => {
			setLoading(true);
			setQueryCombinations(
				getAllcombinationFromQueryform(
					values,
					selectedContractPeriods,
					fielddataDefinitions
				)
			);
		},
	});

	let getContractPeriods = useGetContractPeriods();

	useEffect(() => {
		if (selectedContracts.length) {
			getContractPeriods(
				selectedContracts,
				contractTimeserieFormik.values.timezone
			)
				.then(
					(responses: [ContractPeriodType[], string | undefined]) => {
						const [cp, timezone] = responses;
						setContractPeriods(cp);
						contractTimeserieFormik.setFieldValue(
							"timezone",
							timezone
						);
					}
				)
				.catch((error: string) => {
					console.debug(
						"Error in calling getContractPeriods :",
						error
					);
				});
		}
	}, [selectedContracts]);

	const handleContractSearch = (searchValue: string) => {
		dispatch(updateContractSearch(searchValue));
		setPageInfo((current) => ({
			...current,
			page: 1,
			search: searchValue,
		}));
	};

	const handleContractPeriodSearch = (searchValue: string) => {
		dispatch(updateContractPeriodSearch(searchValue));
	};

	function setFieldValue(name: string, values: any) {
		if (name === "contracts") {
			dispatch(updateContracts(values));
		} else {
			dispatch(updateContractPeriods(values));
		}
	}
	let getTimeseriesDataFromGeco = useGetTimeseriesDataFromGeco();

	useEffect(() => {
		if (queryCombinations.length) {
			getTimeseriesDataFromGeco(
				queryCombinations,
				contractTimeserieFormik.values
			)
				.then((responses) => {
					// will be removed when we add charts
					console.debug("Final Result :", responses);
					setTimeseriesData(responses);
				})
				.catch((error) => {
					console.debug(
						"Error in calling getTimeseriesDataFromGeco :",
						error
					);
				});
		}
	}, [queryCombinations]);

	const chartsOptions = useMemo(() => {
		//@ts-ignore
		const groups = currentView === "ByQuery" ? groupByQueries(timeseriesData) : groupByContract(timeseriesData);
		return Object.keys(groups).map((key: string) => {
			const values: any = groups[key][0]; 
			const chartName = currentView === "ByQuery" ? `${key} (${values?.unit})` : key;
			const series = groups[key].map((val: any) => {
				const points = val.data.map((point: any) => [Number(point.timeStamp * 1000), point.value]);
				return {
					data: points,
					lineWidth: 0.5,
					name: currentView === "ByQuery" ? val.contractName: `${val.queryName} ${val.subQueryName} (${val.unit})`,
					boostThreshold: 5000,
					marker: {
						enabled: false
					},
				};
			});
			
			const options: Highcharts.Options = {
				boost: {
					useGPUTranslations: true,
					seriesThreshold: 1,
				},                
				chart: {
					zooming: {
						type: "x"
					},
				},
				title: {
					text: chartName,
					align: "left"
				},
				tooltip: {
					valueDecimals: 2
				},
				xAxis: {
					type: "datetime",
				},
				//@ts-ignore
				series,
			};
			setLoading(false);
			return options;
		});
	}, [timeseriesData, currentView]);

	return (
		<>
			<Box sx={{ p: "20px 40px" }}>
				<BreadCrumbs></BreadCrumbs>
			</Box>
			<PageTitle
				label="Timeseries [Contract]"
				leftSide={
					<Box display="flex" justifyContent="flex-start">
						<PrimaryButton
							text="Contracts"
							type="submit"
							color="info"
							sx={{
								width: 140,
								color: "#171D21",
							}}
							onClick={getBackToContracts}
						>
							<ChevronLeftIcon />
						</PrimaryButton>
					</Box>
				}
			/>
			<Box
				sx={{ flexGrow: 1, p: "20px 40px", backgroundColor: lightGrey }}
			>
				<Grid container spacing={1}>
					<Grid item xs={3} sx={{ maxWidth: "600px !important" }}>
						<Grid item xs={12}>
							<StyledAccordion expanded={expanded}>
								<StyledAccordionSummary
									data-testid="test-accordion-summary"
									sx={{ background: "white" }}
									aria-controls="tender-content"
									id="tender-header"
									onClick={() => setExpanded(!expanded)}
								>
									<Box sx={style.contractHeader}>
										<Typography>
											Select the contract(s)
										</Typography>

										{expanded ? (
											<ChevronLeftIcon />
										) : (
											<ChevronDownIcon />
										)}
									</Box>
								</StyledAccordionSummary>
								<AccordionDetails sx={style.contractDetails}>
									<CheckboxesTags<{
										id: number;
										name: string;
										start_date: string;
										end_date: string;
									}>
										data-testid="test-contract-selection"
										label="Contract(s)"
										trackBy="id"
										name="contracts"
										value={selectedContracts}
										options={contractItems || []}
										setFieldValue={setFieldValue}
										inputValue={contractSearch}
										onInputChange={handleContractSearch}
									/>

									<CheckboxesTags<{
										id: number;
										name: string;
										start_date: string;
										end_date: string;
									}>
										data-testid="test-contract-period-selection"
										label="Contract Period(s)"
										trackBy="id"
										name="contract_periods"
										value={selectedContractPeriods}
										options={contractPeriods.map(
											(contractPeriod) => {
												return {
													id: contractPeriod.id,
													name: contractPeriod.name,
													start_date:
														contractPeriod.start_date,
													end_date:
														contractPeriod.end_date,
													contractId: contractPeriod.contractId,
													contractName: contractPeriod.contractName
												};
											}
										)}
										setFieldValue={setFieldValue}
										inputValue={contractPeriodSearch}
										onInputChange={
											handleContractPeriodSearch
										}
									/>
								</AccordionDetails>
							</StyledAccordion>
						</Grid>
						<Grid item xs={12} mt={2} minHeight={"400px"}>
							<QueryBuilder
								formik={contractTimeserieFormik}
								fielddataDefinitions={fielddataDefinitions}
							/>
						</Grid>
					</Grid>
					<Grid container item xs={9}  spacing={2} sx={{ alignSelf: "flex-start" }}>
						<>
							<Grid item xs={4} sx={{height: "160px"}}>
								<DateRangeSelector
									timezone={
										contractTimeserieFormik.values.timezone
									}
									dateRange={
										contractTimeserieFormik.values.dateRange
									}
									setFieldValue={
										contractTimeserieFormik.setFieldValue
									}
									formik={contractTimeserieFormik}
								/>
							</Grid>
							<Grid item xs={8} sx={{height: "160px"}}>
								<ViewTypeSelector currentView={currentView} setCurrentView={setCurrentView}></ViewTypeSelector>
							</Grid>
							<If condition={loading}>
								<Grid item container alignItems="center" justifyContent="center" xs={12} sx={{minHeight: "50vh" }}>
									<CircularProgress
										color="inherit"
										size={60}
									/>								
								</Grid>
							</If>
							<If condition={!loading}>
								{chartsOptions.map((chartOption) => (
									<Grid item xs={6} key={chartOption.title?.text}>
										<HighchartsReact
											highcharts={Highcharts}
											options={chartOption}
										/>
									</Grid>
								))}
							</If>
						</>
					</Grid>
				</Grid>
			</Box>
		</>
	);
};

export default TimeseriesPage;
